import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { Resource } from '../api/useFetchResources';
import { multiKeySort } from '../../utils/sort';
import { logger } from '../../utils/logger';

export function useResourceSelection(resources: Resource[], resourceType: string) {
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [savedSelectedResources, setSavedSelectedResources] = useState<Map<string, Resource>>(
    new Map()
  );
  const [resourceTypeCounts, setResourceTypeCounts] = useState<Map<string, number>>(new Map());

  const prevResourceTypeRef = useRef<string>('');

  // When resource type changes, save previous selections and load new ones
  useEffect(() => {
    const prevType = prevResourceTypeRef.current;

    if (prevType && prevType !== resourceType) {
      const prevSelectedIds = selectedResourceIds;
      const toSave = resources.filter((r) => prevSelectedIds.includes(r.id) && r.type === prevType);

      setSavedSelectedResources((prevSaved) => {
        const next = new Map(prevSaved);
        // Remove all from previous type
        Array.from(prevSaved.values()).forEach((r) => {
          if (r.type === prevType) next.delete(r.id);
        });
        toSave.forEach((r) => next.set(r.id, r));
        return next;
      });

      // Load selections for the new type
      const savedForNewType = Array.from(savedSelectedResources.values())
        .filter((r) => r.type === resourceType)
        .map((r) => r.id);
      setSelectedResourceIds(savedForNewType);
    }

    prevResourceTypeRef.current = resourceType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, resources]);

  // Track total count of available resources per type
  useEffect(() => {
    if (!resourceType || resources.length === 0) return;
    const count = resources.filter((r) => r.type === resourceType).length;
    if (count > 0) {
      setResourceTypeCounts((prev) => new Map(prev).set(resourceType, count));
    }
  }, [resources, resourceType]);

  // Sync savedSelectedResources when selectedResourceIds changes
  useEffect(() => {
    if (!resourceType) return;

    const currentTypeResources = new Map<string, Resource>();
    selectedResourceIds.forEach((id) => {
      const resource = resources.find((r) => r.id === id && r.type === resourceType);
      if (resource) currentTypeResources.set(id, resource);
    });

    setSavedSelectedResources((prev) => {
      const next = new Map(prev);
      Array.from(prev.values()).forEach((r) => {
        if (r.type === resourceType) next.delete(r.id);
      });
      currentTypeResources.forEach((r, id) => next.set(id, r));
      return next;
    });
  }, [selectedResourceIds, resourceType, resources]);

  const handleResourceToggle = useCallback((id: string) => {
    setSelectedResourceIds((prev) => {
      const isAdding = !prev.includes(id);
      logger.debug('Resource toggled', {
        resourceId: id,
        action: isAdding ? 'selected' : 'deselected',
        totalSelected: isAdding ? prev.length + 1 : prev.length - 1,
        resourceType
      });
      return isAdding ? [...prev, id] : prev.filter((x) => x !== id);
    });
  }, [resourceType]);

  const handleSelectAllToggle = useCallback((ids: string[]) => {
    const allIdsSet = new Set(ids);
    setSelectedResourceIds((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));
      const newSelection = allSelected
        ? prev.filter((id) => !allIdsSet.has(id))
        : [...new Set([...prev, ...ids])];
      logger.info('Select all toggled', {
        action: allSelected ? 'deselected' : 'selected',
        count: ids.length,
        totalSelected: newSelection.length,
        resourceType
      });
      return newSelection;
    });
  }, [resourceType]);

  const handleResetSelections = useCallback(
    (scope: 'all' | 'current' = 'all') => {
      logger.info('Resource selections reset', {
        scope,
        previousCount: selectedResourceIds.length,
        resourceType
      });
      if (scope === 'all') {
        setSavedSelectedResources(new Map());
        setSelectedResourceIds([]);
        setResourceTypeCounts(new Map());
      } else {
        setSavedSelectedResources((prev) => {
          const next = new Map(prev);
          Array.from(next.values()).forEach((r) => {
            if (r.type === resourceType) next.delete(r.id);
          });
          return next;
        });
        setSelectedResourceIds([]);
        setResourceTypeCounts((prev) => {
          const next = new Map(prev);
          next.delete(resourceType);
          return next;
        });
      }
    },
    [resourceType, selectedResourceIds.length]
  );

  const allSelectedResources = useMemo(() => {
    // Start with all saved selections (these persist across type changes)
    const combined = new Map(savedSelectedResources);

    // Add currently selected resources from the current resources array
    selectedResourceIds.forEach((id) => {
      const resource = resources.find((r) => r.id === id);
      if (resource) {
        combined.set(id, resource);
      }
      // If resource not found in current array but ID is selected,
      // it should already be in savedSelectedResources, so keep it
    });

    return multiKeySort(Array.from(combined.values()), ['type', 'name']);
  }, [savedSelectedResources, resources, selectedResourceIds]);

  return {
    selectedResourceIds,
    setSelectedResourceIds,
    savedSelectedResources,
    handleResourceToggle,
    handleSelectAllToggle,
    handleResetSelections,
    allSelectedResources,
    resourceTypeCounts
  } as const;
}

export default useResourceSelection;
